import React from "react";
import { Plus, Trash2 } from "lucide-react";

export default function CategoryRulesSection({
  categoryRules,
  categoryOptions = [],
  isEditing,
  saving,
  canManage,
  onStartEdit,
  onCancelEdit,
  onSave,
  onChange,
  onAddRow,
  onRemoveRow,
}) {
  const allCategoryOptions = Array.from(
    new Set([
      ...(Array.isArray(categoryOptions) ? categoryOptions : []),
      ...(Array.isArray(categoryRules) ? categoryRules : [])
        .map((row) => String(row?.category || "").trim())
        .filter(Boolean),
    ]),
  );

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
      {/* Header */}
      <div className="flex flex-row justify-between items-start mb-8">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold text-gray-900">
            Category Pricing Rules
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Define premiums and margins by category to standardize SRP calculations.
          </p>
        </div>

        <div className="flex items-center gap-4">
          {isEditing ? (
            <>
              <button
                onClick={onCancelEdit}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || !canManage}
                className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-3 transition-all shadow-lg active:scale-98 ${
                  saving
                    ? "bg-gray-400 text-gray-100 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              onClick={onStartEdit}
              disabled={!canManage}
              className={`px-6 py-3 font-semibold rounded-2xl transition-all shadow-lg active:scale-98 ${
                canManage
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-2xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Category</th>
              <th className="px-4 py-3 text-left font-semibold">
                Signature Premium / Unit
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Allergen Premium / Unit
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Min Retail SRP Margin
              </th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(categoryRules) ? categoryRules : []).map((row, index) => (
              <tr key={`category-rule-${index}`} className="border-t border-gray-200">
                <td className="px-4 py-3">
                  <select
                    value={row.category || ""}
                    onChange={(e) => onChange(index, "category", e.target.value)}
                    disabled={!isEditing}
                    className={`w-full rounded-xl px-3 py-2 border ${
                      isEditing
                        ? "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                        : "appearance-none border-transparent bg-transparent text-gray-700"
                    }`}
                  >
                    <option value="">Select category</option>
                    {allCategoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={row.signaturePremiumPerUnit ?? 0}
                    onChange={(e) =>
                      onChange(index, "signaturePremiumPerUnit", e.target.value)
                    }
                    disabled={!isEditing}
                    className={`w-full rounded-xl px-3 py-2 border ${
                      isEditing
                        ? "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                        : "border-transparent bg-transparent text-gray-700"
                    }`}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={row.allergenPremiumPerUnit ?? 0}
                    onChange={(e) =>
                      onChange(index, "allergenPremiumPerUnit", e.target.value)
                    }
                    disabled={!isEditing}
                    className={`w-full rounded-xl px-3 py-2 border ${
                      isEditing
                        ? "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                        : "border-transparent bg-transparent text-gray-700"
                    }`}
                  />
                </td>
                <td className="px-4 py-3">
                  {isEditing ? (
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.001"
                        value={row.minRetailSrpMargin ?? 0}
                        onChange={(e) =>
                          onChange(index, "minRetailSrpMargin", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full rounded-xl px-3 py-2 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium pointer-events-none">
                        %
                      </span>
                    </div>
                  ) : (
                    <div className="px-3 py-2 text-gray-700 font-medium">
                      {((row.minRetailSrpMargin ?? 0) * 100).toFixed(1)}%
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(index)}
                    disabled={!isEditing}
                    className={`inline-flex items-center justify-center p-2 rounded-lg ${
                      isEditing
                        ? "text-red-600 hover:bg-red-50"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Row Button */}
      <div className="mt-4">
        <button
          type="button"
          onClick={onAddRow}
          disabled={!isEditing}
          className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            isEditing
              ? "bg-black text-white hover:bg-gray-800"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          <Plus className="w-4 h-4" />
          Add Row
        </button>
      </div>
    </div>
  );
}